/**
 * Util function that checks flight items, flight views, approval request and approval policy
 * to determine if the approval request restore in the checkout is valid
 */
import { FlightSearchBudgets } from 'lib/flights/flightUtils'
import { getMaxAllowedBudget, isAllDatesMatchApprovalRequest, isRouteMatchApprovalRequest } from './approvalRequestUtils'
import { calculateFlightBudget, FlightBudgetInfo } from './calculateTripRequestBudget'
import { isOverBudget } from './budgetComparison'

export enum FlightApprovalInvalidReason {
  ROUTE = 'route',
  DATE = 'date',
  BUDGET = 'budget',
}

type GetFlightApprovalInvalidReasonParams = {
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined
  approvalPolicy: App.BusinessTraveller.ApprovalPolicy | null
  flightItems: Array<App.Checkout.FlightItem>
  flightViews: Array<App.Checkout.FlightItemView>
  flightSearchBudgets: Array<FlightSearchBudgets>
}

export type FlightApprovalValidationResult =
  | { invalidReason: FlightApprovalInvalidReason.ROUTE | FlightApprovalInvalidReason.DATE | undefined }
  | { invalidReason: FlightApprovalInvalidReason.BUDGET; flightBudgetInfo: FlightBudgetInfo }

export function getFlightApprovalInvalidReason(params: GetFlightApprovalInvalidReasonParams): FlightApprovalValidationResult {
  const { approvalRequest, approvalPolicy, flightItems, flightViews, flightSearchBudgets } = params

  if (!approvalRequest || flightItems.length === 0 || flightViews.length === 0) {
    return { invalidReason: undefined }
  }

  if (!isRouteMatchApprovalRequest(approvalRequest, flightItems, flightViews)) {
    return { invalidReason: FlightApprovalInvalidReason.ROUTE }
  }

  if (!isAllDatesMatchApprovalRequest(approvalRequest, flightItems, flightViews)) {
    return { invalidReason: FlightApprovalInvalidReason.DATE }
  }

  // Check if the restored flight item total cost exceeds the (approved cost + price buffer)
  if (approvalPolicy) {
    const maxAllowedBudget = getMaxAllowedBudget(approvalRequest.total, approvalPolicy.priceBuffer)
    if (maxAllowedBudget > 0) {
      const flightBudgetInfo = calculateFlightBudget({
        flightViews,
        flightSearchBudgets,
        flightBudget: maxAllowedBudget,
      })
      const isOverMaxAllowedBudget = isOverBudget(flightBudgetInfo.budgetedFlightTotalWithOtherFees, maxAllowedBudget)
      const isApprovalRequired = approvalPolicy.scope === 'ALWAYS' || approvalPolicy.scope === 'OUTOFPOLICYONLY'
      // If the flight item total cost exceeds the (approved cost + price buffer) and approval is required, return the invalid reason
      if (isOverMaxAllowedBudget && isApprovalRequired) {
        return { invalidReason: FlightApprovalInvalidReason.BUDGET, flightBudgetInfo }
      }
    }
  }

  return { invalidReason: undefined }
}
